type TranslationFunction = (key: string) => string;

type TypedError = {
  response?: {
    status?: number;
    data?: { message?: string; error?: string; correlationId?: string; correlation_id?: string; correlationID?: string; [key: string]: unknown };
    headers?: Record<string, string>;
  };
  message?: string;
  code?: string;
  status?: number;
  data?: { correlationId?: string; correlation_id?: string; error?: string; [key: string]: unknown };
};

/**
 * Error response type with correlation ID
 */
export interface ErrorResponse {
  message: string;
  correlationId?: string;
  isServerError: boolean;
}

export const getErrorMessageByStatusCode = (statusCode: number, _t: TranslationFunction): string => {
  if (statusCode >= 400 && statusCode < 500) {
    return 'Resource not found';
  }
  return 'Something went wrong, please try again';
};

export const extractCorrelationId = (error: unknown): string | undefined => {
  const err = error as TypedError;
  return (
    err?.response?.data?.correlationId ||
    err?.response?.data?.correlation_id ||
    err?.response?.data?.correlationID ||
    err?.response?.headers?.['x-correlation-id'] ||
    err?.data?.correlationId ||
    err?.data?.correlation_id
  );
};

export const getEnhancedApiErrorMessage = (
  error: unknown,
  _t?: TranslationFunction,
  _fallbackKey: string = 'errors.genericError'
): ErrorResponse => {
  const err = error as TypedError;
  const statusCode = err?.response?.status || err?.status;
  const backendMessage = err?.response?.data?.error || err?.response?.data?.message || (err?.data?.error as string | undefined);
  const correlationId = extractCorrelationId(error);

  if (statusCode !== undefined && statusCode >= 500) {
    return { message: 'Something went wrong', correlationId, isServerError: true };
  }

  if (statusCode !== undefined && statusCode >= 400 && statusCode < 500) {
    if (backendMessage && typeof backendMessage === 'string') {
      return { message: backendMessage, isServerError: false };
    }
    return { message: 'Something went wrong, please try again', isServerError: false };
  }

  if (!err?.response) {
    return { message: 'No internet connection available', isServerError: false };
  }

  return { message: 'Something went wrong, please try again', isServerError: false };
};

export const getApiErrorMessage = (
  error: unknown,
  _t: TranslationFunction,
  _fallbackKey: string = 'errors.genericError'
): string => {
  const err = error as TypedError;
  if (err?.status) {
    const statusCode = err.status;
    const backendMessage = err?.data?.error as string | undefined;
    if (backendMessage) {
      if (backendMessage.toLowerCase().includes('otp code is incorrect') ||
          (backendMessage.toLowerCase().includes('otp') && backendMessage.toLowerCase().includes('expired'))) {
        return 'There is some issue with OTP code. Please request a new code';
      }
      if (backendMessage.toLowerCase().includes('checkout from traveler is required')) return backendMessage;
      if (backendMessage.toLowerCase().includes('traveler') && backendMessage.toLowerCase().includes('not yet checked out')) {
        return 'Traveler has not yet checked out';
      }
      if (backendMessage.toLowerCase().includes('jpg') || backendMessage.toLowerCase().includes('jpeg') ||
          backendMessage.toLowerCase().includes('png') || backendMessage.toLowerCase().includes('webp')) {
        return backendMessage;
      }
      if (backendMessage.toLowerCase().includes('already checked out')) return backendMessage;
    }
    return getErrorMessageByStatusCode(statusCode, _t);
  }
  if (!err?.response || err?.code === 'ERR_NETWORK' || (err?.message?.toLowerCase().includes('network') ?? false)) {
    return 'No internet connection. Please check your connection and try again';
  }
  if (err?.message) {
    if (err.message.toLowerCase().includes('timeout')) return 'Something went wrong, please try again';
  }
  return 'Something went wrong, please try again';
};

export const isNetworkError = (error: unknown): boolean => {
  const err = error as TypedError;
  return (!err?.response || (err?.message?.toLowerCase().includes('network') ?? false) || err?.code === 'ERR_NETWORK');
};

export const isTimeoutError = (error: unknown): boolean => {
  const err = error as TypedError;
  return (err?.response?.status === 408 || (err?.message?.toLowerCase().includes('timeout') ?? false) || err?.code === 'ECONNABORTED');
};

export const isAuthError = (error: unknown): boolean => {
  const err = error as TypedError;
  return err?.response?.status === 401 || err?.response?.status === 403;
};

export const handleApiError = (error: unknown): string => {
  const err = error as TypedError;
  const backendMessage = err?.response?.data?.message || err?.response?.data?.error;
  if (backendMessage) {
    const msg = String(backendMessage);
    if (msg.toLowerCase().includes('otp code is incorrect') ||
        (msg.toLowerCase().includes('otp') && msg.toLowerCase().includes('expired'))) {
      return 'There is some issue with OTP code. Please request a new code';
    }
    if (msg.toLowerCase().includes('checkout from traveler is required')) return msg;
    if (msg.toLowerCase().includes('traveler') && msg.toLowerCase().includes('not yet checked out')) return 'Traveler has not yet checked out';
    if (msg.toLowerCase().includes('jpg') || msg.toLowerCase().includes('jpeg') ||
        msg.toLowerCase().includes('png') || msg.toLowerCase().includes('webp')) return msg;
    if (msg.toLowerCase().includes('already checked out')) return msg;
    if (msg.length < 150 && !msg.includes('Error:')) return msg;
  }
  if (err?.response?.status) {
    const statusCode = err.response.status;
    if (statusCode === 404) return 'Resource not found';
    if (statusCode === 400) return 'Invalid request. Please check your input and try again';
    if (statusCode === 401 || statusCode === 403) return 'You are not authorized to perform this action';
    if (statusCode >= 400 && statusCode < 500) return 'Something went wrong. Please try again';
  }
  if (err?.message) {
    if (err.message.toLowerCase().includes('network')) return 'Network error. Please check your connection and try again';
    if (err.message.toLowerCase().includes('timeout')) return 'Request timed out. Please try again';
  }
  return 'Something went wrong. Please try again';
};
